import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import './UnityGame.css';
import {
  FaExpand,
  FaCompress,
  FaChevronRight,
  FaChevronLeft,
  FaVideo,
  FaEyeSlash,
  FaSpinner,
  FaDesktop,
} from 'react-icons/fa';
import IVSRealtimePanel from './IVSRealtimePanel';
import IVSRealtimeChat, {
  type ClapSignal,
  type HandRaiseSignal,
  type IVSRealtimeChatRef,
} from './IVSRealtimeChat';
import RoomSelector, { RoomSelectorRef } from './RoomSelector';
import ModTools from './ModTools';
import Agenda from './Agenda';
import SocialInteraction from './SocialInteraction';
import { BreakoutClapDock, BreakoutClapOverlay } from './BreakoutClap';
import UnityGameDockApp from './UnityGameDockApp';
import HelpWidget from './HelpWidget';
import RoomUsersWidget from './RoomUsersWidget';
import StagePlayer from './StagePlayer';
import NPCDialog from './NPCDialog';
import LeavePodium from './LeavePodium';
import OnboardingTour, { hasCompletedOnboarding, resetOnboarding } from './OnboardingTour';
import { resetScreenShareTutorial } from './ScreenShareTour';
import API from '../../API';
import { FocusProvider, useFocus, useOverlay } from '../../contexts/FocusContext';
import type { LocalVideoControlsApi } from './localVideoControlsApi';
import type { ScreenShareControlsApi, ScreenShareQuality } from './screenShareControlsApi';
import { SCREEN_SHARE_QUALITY_PRESETS } from './screenShareControlsApi';
import { DockLocalVideoPreview } from './dockLocalVideoPreview';

// Import loading images
import intersectLoadingImage01 from '../images/intersect-test-loading-01.png';
import intersectLoadingImage02 from '../images/intersect-test-loading-02.png';
import intersectLoadingImage03 from '../images/intersect-test-loading-03.png';
import intersectLoadingImage04 from '../images/intersect-test-loading-04.png';
import intersectLoadingImage05 from '../images/intersect-test-loading-05.png';

// Import controls image
import tutorialPanel from '../images/tutorial-widget-v3.png';

// Import avatar selection images
import avatarIcon54 from '../images/intersect-avatar-icon-54.png';
import avatarIcon55 from '../images/intersect-avatar-icon-55.png';
import avatarIcon56 from '../images/intersect-avatar-icon-56.png';
import avatarIcon57 from '../images/intersect-avatar-icon-57.png';
import avatarIcon58 from '../images/intersect-avatar-icon-58.png';
import avatarIcon59 from '../images/intersect-avatar-icon-59.png';
import avatarIcon60 from '../images/intersect-avatar-icon-60.png';
import avatarIcon61 from '../images/intersect-avatar-icon-61.png';
import avatarIcon62 from '../images/intersect-avatar-icon-62.png';
import avatarIcon63 from '../images/intersect-avatar-icon-63.png';

// WebGPU type declarations
declare global {
  interface Navigator {
    gpu?: {
      requestAdapter(): Promise<GPUAdapter | null>;
      getPreferredCanvasFormat(): GPUTextureFormat;
    };
  }
  
  interface GPUAdapter {
    requestDevice(): Promise<GPUDevice>;
  }
  
  interface GPUDevice {
    createCommandEncoder(): GPUCommandEncoder;
    queue: GPUQueue;
  }
  
  interface GPUCommandEncoder {
    beginRenderPass(descriptor: GPURenderPassDescriptor): GPURenderPassEncoder;
    finish(): GPUCommandBuffer;
  }
  
  interface GPURenderPassEncoder {
    end(): void;
  }
  
  interface GPUCommandBuffer {}
  
  interface GPUQueue {
    submit(commandBuffers: GPUCommandBuffer[]): void;
  }
  
  interface GPURenderPassDescriptor {
    colorAttachments: GPURenderPassColorAttachment[];
  }
  
  interface GPURenderPassColorAttachment {
    view: GPUTextureView;
    clearValue?: { r: number; g: number; b: number; a: number };
    loadOp: 'clear' | 'load';
    storeOp: 'store';
  }
  
  interface GPUTextureView {}
  
  type GPUTextureFormat = string;
  
  interface HTMLCanvasElement {
    getContext(contextId: 'webgpu', options?: GPUCanvasConfiguration): GPUCanvasContext | null;
  }
  
  interface GPUCanvasContext {
    configure(configuration: GPUCanvasConfiguration): void;
    getCurrentTexture(): GPUTexture;
  }
  
  interface GPUCanvasConfiguration {
    device: GPUDevice;
    format: GPUTextureFormat;
    alphaMode?: 'opaque' | 'premultiplied';
  }
  
  interface GPUTexture {
    createView(): GPUTextureView;
  }
}

// Loading images array
const loadingImages = [
  intersectLoadingImage01,
  intersectLoadingImage02,
  intersectLoadingImage03,
  intersectLoadingImage04,
  intersectLoadingImage05
];

interface UnityGameSandboxProps {
  ivsStageArn?: string;
  ivsParticipantToken?: string;
  accessToken?: string;
}

// Default access token for sandbox testing
const SANDBOX_ACCESS_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY3MzciLCJpYXQiOjE3Njk1Njg3MDUsImV4cCI6MTc3MjE2MDcwNX0.akC5p0ql_j84pRblpd33w6kaMwz4Z4XzO1OX-lvFz2M';

// ========================================
// CONFIGURATION - Easy to modify defaults
// ========================================
const DEFAULT_PANEL_ENABLED = true;   // Set to true to show panel on load, false to start minimized
const MOCK_MODE = true;               // Set to true to bypass API calls and use mock data for testing UI

// Mock data for testing without valid access token
const MOCK_IVS_DATA = {
  participantToken: 'mock-participant-token-for-testing',
  stageArn: 'arn:aws:ivs:us-west-2:000000000000:stage/mock-stage',
  userId: 'sandbox-test-user',
  /** Shown for raise-hand / People when not overridden by live IVS participant data */
  displayName: 'Sandbox member',
  isIntersectStaff: true, // Set to true so we can test staff features
  chatRoomArn: 'arn:aws:ivschat:us-west-2:000000000000:room/mock-room',
};

const UnityGameSandboxInner: React.FC<UnityGameSandboxProps> = ({ 
  ivsStageArn: propIvsStageArn, 
  ivsParticipantToken: propIvsParticipantToken,
  accessToken: propAccessToken
}) => {
  const { hasActiveOverlay } = useFocus();
  // Use provided access token or default sandbox token
  const accessToken = propAccessToken || SANDBOX_ACCESS_TOKEN;
  
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageOpacity, setImageOpacity] = useState(1);
  const [showControlsPopup, setShowControlsPopup] = useState(false);
  const [showAvatarSelection, setShowAvatarSelection] = useState(true); // Avatar selection modal
  const [isCustomizingAvatar, setIsCustomizingAvatar] = useState(false); // Loading state for avatar customization
  const [showOnboarding, setShowOnboarding] = useState(false); // Onboarding tour
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isIVSPanelCollapsed, setIsIVSPanelCollapsed] = useState(true); // true = minimized to dock Video tile
  const [isIVSPanelEnabled, setIsIVSPanelEnabled] = useState(DEFAULT_PANEL_ENABLED);
  const [isIntersectStaff, setIsIntersectStaff] = useState(false);
  const [debugMode, setDebugMode] = useState(true); // Debug mode enabled in sandbox
  const [debugIntersectStaff, setDebugIntersectStaff] = useState(false); // Manual override for testing
  const [testInputValue, setTestInputValue] = useState(''); // Test input for debugging
  const [isUnityWorldReady, setIsUnityWorldReady] = useState(true); // Track Unity world loaded state (default true in sandbox for testing)
  const [isUIVisible, setIsUIVisible] = useState(true); // Track UI visibility (toggle with H key)
  const [isAtPodium, setIsAtPodium] = useState(false); // Track if user is at podium
  const [hasScreenSharePermission, setHasScreenSharePermission] = useState(false); // Track if user has been granted screen share permission
  const screenShareAutoGrantedForRoom = useRef<string | null>(null); // Track which room auto-granted screenshare
  const [localVideoControlApi, setLocalVideoControlApi] = useState<LocalVideoControlsApi | null>(null);
  const [screenShareControlApi, setScreenShareControlApi] = useState<ScreenShareControlsApi | null>(null);
  const [videoDockPopoverOpen, setVideoDockPopoverOpen] = useState(false);
  const [screenShareDockPopoverOpen, setScreenShareDockPopoverOpen] = useState(false);
  const videoDockPopoverOwnerId = useMemo(() => `dock-popover-video-${Math.random().toString(36).slice(2)}`, []);
  const screenShareDockPopoverOwnerId = useMemo(
    () => `dock-popover-screenshare-${Math.random().toString(36).slice(2)}`,
    []
  );
  const videoDockTileRef = useRef<HTMLDivElement>(null);
  const screenShareDockTileRef = useRef<HTMLDivElement>(null);
  const videoCameraPreviewHostRef = useRef<HTMLDivElement>(null);
  const localVideoControlApiRef = useRef<LocalVideoControlsApi | null>(null);
  const videoPreviewOpenedForPopoverRef = useRef(false);

  localVideoControlApiRef.current = localVideoControlApi;

  const handleScreenShareTutorialRequest = useCallback(() => {
    setScreenShareDockPopoverOpen(true);
  }, []);

  const openVideoDockFromFacePin = useCallback(() => {
    if (!isIVSPanelEnabled) {
      console.log('[Sandbox IVS Panel] Re-enabling panel and rejoining stream');
      setIsIVSPanelEnabled(true);
    }
    setIsIVSPanelCollapsed(true);
    setScreenShareDockPopoverOpen(false);
    setVideoDockPopoverOpen(true);
  }, [isIVSPanelEnabled]);
  // In mock mode, use mock data; otherwise use props or null
  const [ivsParticipantToken, setIvsParticipantToken] = useState<string | null>(
    propIvsParticipantToken || (MOCK_MODE ? MOCK_IVS_DATA.participantToken : null)
  );
  const [ivsStageArn, setIvsStageArn] = useState<string | null>(
    propIvsStageArn || (MOCK_MODE ? MOCK_IVS_DATA.stageArn : null)
  );
  const [ivsUserId, setIvsUserId] = useState<string | null>(MOCK_MODE ? MOCK_IVS_DATA.userId : null);
  const [ivsIsIntersectStaff, setIvsIsIntersectStaff] = useState<boolean>(MOCK_MODE ? MOCK_IVS_DATA.isIntersectStaff : false);
  const [isLoadingIVS, setIsLoadingIVS] = useState(false);
  const hasFetchedIVSToken = useRef(MOCK_MODE); // Skip fetching in mock mode
  
  // Room state
  const [currentRoomId, setCurrentRoomId] = useState<string>('intersect-main');
  const [currentRoomName, setCurrentRoomName] = useState<string>('Intersect Main Room');
  const [chatRoomArn, setChatRoomArn] = useState<string>(MOCK_MODE ? MOCK_IVS_DATA.chatRoomArn : 'arn:aws:ivschat:us-west-2:390409532441:room/A02aYaQTK0b2');
  const [stageArn, setStageArn] = useState<string>(MOCK_MODE ? MOCK_IVS_DATA.stageArn : 'arn:aws:ivs:us-west-2:390409532441:stage/uMXabyK915IH');
  const [stagePlaybackUrl, setStagePlaybackUrl] = useState<string>('');
  const [eventId] = useState<string>('22222');
  const [isRoomTransitioning, setIsRoomTransitioning] = useState<boolean>(false);
  
  // Stage view mode
  const [isStageViewMode, setIsStageViewMode] = useState(false);
  const [isStageMuted, setIsStageMuted] = useState(true); // Stage audio muted by default
  const [isLocalUserScreenSharing, setIsLocalUserScreenSharing] = useState(false); // Mute stage playback when presenting to avoid double audio
  
  // Low resolution mode for resource saving
  const [isLowResMode, setIsLowResMode] = useState(false);
  
  // Presenter mode - when user is at podium and in low-res mode (focused for screensharing)
  const isPresenterMode = isAtPodium && isLowResMode;
  
  // Fall detection and respawn
  const [showRespawnPopup, setShowRespawnPopup] = useState(false);
  const [showRespawnBanner, setShowRespawnBanner] = useState(false);
  const lastYPositionRef = useRef<number | null>(null);
  const hasFallPopupShownRef = useRef<boolean>(false);
  
  // NPC Dialog state
  const [showNPCDialog, setShowNPCDialog] = useState(false);
  const [currentNPCId, setCurrentNPCId] = useState<string>('');
  const [currentNPCMessageIndex, setCurrentNPCMessageIndex] = useState(0);

  const [handRaiseByUserId, setHandRaiseByUserId] = useState<
    Record<string, { displayName: string; raisedAt: number }>
  >({});

  const [clapBursts, setClapBursts] = useState<
    Array<{ id: string; displayName: string; leftPct: number; topPct: number; dx: string; dy: string }>
  >([]);

  const handleHandRaiseSignal = useCallback((e: HandRaiseSignal) => {
    if (e.raised) {
      setHandRaiseByUserId((prev) => ({
        ...prev,
        [e.userId]: { displayName: e.displayName, raisedAt: e.at },
      }));
    } else {
      setHandRaiseByUserId((prev) => {
        if (!(e.userId in prev)) return prev;
        const next = { ...prev };
        delete next[e.userId];
        return next;
      });
    }
  }, []);

  const handRaiseNotificationItems = useMemo(
    () =>
      Object.entries(handRaiseByUserId).map(([userId, v]) => ({
        userId,
        displayName: v.displayName,
        raisedAt: v.raisedAt,
      })),
    [handRaiseByUserId]
  );

  const localUserIdsForHandRaise = useMemo(() => {
    const s = ivsUserId != null ? String(ivsUserId).trim() : '';
    return s ? [s] : [];
  }, [ivsUserId]);

  const isLocalHandRaisedInQueue = useMemo(() => {
    return localUserIdsForHandRaise.some((id) =>
      Object.prototype.hasOwnProperty.call(handRaiseByUserId, id)
    );
  }, [handRaiseByUserId, localUserIdsForHandRaise]);

  const handleModeratorUnraiseHand = useCallback(
    async (targetUserId: string) => {
      if (MOCK_MODE) {
        handleHandRaiseSignal({
          userId: targetUserId,
          displayName: '',
          raised: false,
          at: Date.now(),
        });
        return;
      }
      const chat = chatRef.current;
      if (!chat?.sendModeratorUnraiseHand) {
        throw new Error('Chat unavailable');
      }
      await chat.sendModeratorUnraiseHand(targetUserId);
    },
    [MOCK_MODE, handleHandRaiseSignal]
  );

  const handleClapSignal = useCallback((e: ClapSignal) => {
    const id = `${e.at}-${Math.random().toString(36).slice(2, 10)}`;
    const leftPct = 8 + Math.random() * 78;
    const topPct = 12 + Math.random() * 58;
    const dx = `${(Math.random() - 0.5) * 200}px`;
    const dy = `${-70 - Math.random() * 100}px`;

    setClapBursts((prev) => [...prev, { id, displayName: e.displayName, leftPct, topPct, dx, dy }]);
    window.setTimeout(() => {
      setClapBursts((prev) => prev.filter((b) => b.id !== id));
    }, 3200);
  }, []);

  const handRaiseDisplayName = useMemo(
    () => (MOCK_MODE ? MOCK_IVS_DATA.displayName : undefined),
    []
  );

  const handleSandboxBreakoutClap = useCallback(async () => {
    if (MOCK_MODE) {
      await new Promise((r) => setTimeout(r, 100));
      handleClapSignal({
        userId: ivsUserId || 'sandbox-local',
        displayName: handRaiseDisplayName || 'You',
        at: Date.now(),
      });
      return;
    }
    const chat = chatRef.current;
    if (!chat?.sendBreakoutClap) throw new Error('Chat unavailable');
    await chat.sendBreakoutClap();
  }, [handRaiseDisplayName, ivsUserId, handleClapSignal]);

  useEffect(() => {
    setHandRaiseByUserId({});
    setClapBursts([]);
  }, [currentRoomId]);

  useEffect(() => {
    if (MOCK_MODE || !accessToken || !eventId || !currentRoomId) return;
    const pathParams = { eventId, roomId: currentRoomId };
    const presenceUrl = `${process.env.REACT_APP_API_URL}/events/${encodeURIComponent(
      eventId
    )}/rooms/${encodeURIComponent(currentRoomId)}/presence`;
    const heartbeat = () => {
      API.postRoomPresence(accessToken, pathParams).catch(() => {});
    };
    heartbeat();
    const interval = window.setInterval(heartbeat, 35000);

    const leavePresenceKeepalive = () => {
      fetch(presenceUrl, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener('pagehide', leavePresenceKeepalive);

    return () => {
      window.removeEventListener('pagehide', leavePresenceKeepalive);
      clearInterval(interval);
      API.deleteRoomPresence(accessToken, pathParams).catch(() => {});
    };
  }, [accessToken, eventId, currentRoomId]);

  // NPC dialogue messages - map NPC IDs to their specific dialogues
  const npcDialogues: { [key: string]: string[] } = {
    'npc-1': [
      "Welcome to the Intersect Virtual Hub.",
      "Feel free to explore all the breakout rooms.",
      "Feel free to ask staff for help at any time."
    ],
    'npc-2': ["Have you checked what's happening today?"],
    'npc-3': ["Remember, you can ask staff for help at any time."],
    'npc-4': ["Enjoy the event and make the most of your experience!"],
    'npc-5': ["Welcome! Feel free to explore all the breakout rooms."],
    'npc-6': ["Don't forget to check out the stage presentations!"]
  };

  // Register modals as overlays to prevent Unity from recapturing input
  useOverlay('controls-popup', showControlsPopup);
  useOverlay('avatar-selection', showAvatarSelection);
  useOverlay('respawn-popup', showRespawnPopup);
  useOverlay('npc-dialog', showNPCDialog);
  useOverlay('unity-dock-video-popover', videoDockPopoverOpen);
  useOverlay('unity-dock-screenshare-popover', screenShareDockPopoverOpen);

  useEffect(() => {
    if (!videoDockPopoverOpen && !screenShareDockPopoverOpen) {
      return undefined;
    }
    const onMouseDown = (event: MouseEvent) => {
      const t = event.target as Node;
      const inVideoTile = videoDockTileRef.current?.contains(t);
      const inVideoPopover = (t as Element).closest?.(`[data-unity-dock-popover-owner="${videoDockPopoverOwnerId}"]`);
      const inShareTile = screenShareDockTileRef.current?.contains(t);
      const inSharePopover = (t as Element).closest?.(
        `[data-unity-dock-popover-owner="${screenShareDockPopoverOwnerId}"]`
      );
      const onLocalFacePin = (t as Element).closest?.('[data-ff-local-face-pin]');
      if (videoDockPopoverOpen && !inVideoTile && !inVideoPopover && !onLocalFacePin) {
        localVideoControlApiRef.current?.closeCameraPreview?.();
        setVideoDockPopoverOpen(false);
      }
      if (screenShareDockPopoverOpen && !inShareTile && !inSharePopover) {
        setScreenShareDockPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [
    videoDockPopoverOpen,
    screenShareDockPopoverOpen,
    videoDockPopoverOwnerId,
    screenShareDockPopoverOwnerId,
  ]);

  useEffect(() => {
    if (!videoDockPopoverOpen && !screenShareDockPopoverOpen) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        localVideoControlApiRef.current?.closeCameraPreview?.();
        setVideoDockPopoverOpen(false);
        setScreenShareDockPopoverOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [videoDockPopoverOpen, screenShareDockPopoverOpen]);

  useLayoutEffect(() => {
    if (!videoDockPopoverOpen) {
      videoPreviewOpenedForPopoverRef.current = false;
      return;
    }
    if (videoPreviewOpenedForPopoverRef.current) {
      return;
    }
    const api = localVideoControlApiRef.current;
    // Preview uses local getUserMedia only — room video-call token / stage is not required.
    if (!api || api.isCameraPreviewOpen) {
      return;
    }
    // If camera is already on when the popover opens, we skip auto-preview but must still mark this session so
    // turning the camera off does not auto-relaunch preview (ref would otherwise stay false).
    if (api.localVideoEnabled) {
      videoPreviewOpenedForPopoverRef.current = true;
      return;
    }
    videoPreviewOpenedForPopoverRef.current = true;
    void api.openCameraPreview();
  }, [videoDockPopoverOpen, localVideoControlApi]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<IVSRealtimeChatRef | null>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const roomSelectorRef = useRef<RoomSelectorRef>(null);

  // Stub sendMessageToUnity function for sandbox (Unity not actually loaded)
  const sendMessageToUnity = (gameObjectName: string, methodName: string, parameter?: string | number) => {
    if (gameObjectName === 'BrowserBridge' && methodName === 'ReceivePayload' && typeof parameter === 'string') {
      try {
        const outer = JSON.parse(parameter) as { eventType?: string; payload?: string };
        if (
          outer.eventType === 'PlayerMute' ||
          outer.eventType === 'PlayerUnmute' ||
          outer.eventType === 'IvsScreenSharePublisher'
        ) {
          console.info('[ff-voice-bridge]', 'UnityGameSandbox-stub', outer.eventType, 'envelope=', parameter);
          if (outer.payload) {
            try {
              console.info('[ff-voice-bridge]', 'UnityGameSandbox-stub', 'inner', JSON.parse(outer.payload));
            } catch {
              /* ignore inner parse */
            }
          }
        }
      } catch {
        /* not JSON */
      }
    }
    console.log(`[Sandbox] Would send to Unity: GameObject="${gameObjectName}", Method="${methodName}", Param="${parameter}"`);
  };

  const customizeActiveAvatar = async (avatarId: string, characterName: string) => {
    if (isCustomizingAvatar) return; // Prevent multiple clicks
    
    setIsCustomizingAvatar(true);
    console.log(`[Sandbox] Avatar selected: ${avatarId} (${characterName})`);
    
    // Simulate API delay in sandbox
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // In sandbox, just close the modal
    setShowAvatarSelection(false);
    setIsCustomizingAvatar(false);
  };

  // NPC Dialog handlers
  const handleNPCNext = () => {
    const currentMessages = npcDialogues[currentNPCId] || [];
    setCurrentNPCMessageIndex((prev) => {
      const nextIndex = prev + 1;
      if (nextIndex >= currentMessages.length) {
        // If we've reached the end, close the dialog
        setShowNPCDialog(false);
        return 0;
      }
      return nextIndex;
    });
  };

  const handleNPCClose = () => {
    setShowNPCDialog(false);
    setCurrentNPCMessageIndex(0);
  };

  // Test NPC dialog function for sandbox
  const testNPCDialog = () => {
    setCurrentNPCId('npc-1');
    setCurrentNPCMessageIndex(0);
    setShowNPCDialog(true);
  };

  // Handle leaving podium - teleport back to spawn point (sandbox)
  const handleLeavePodium = () => {
    console.log('[Sandbox] Leaving podium, returning to spawn point');
    
    try {
      const teleportData = {
        eventType: 'Teleport',
        payload: JSON.stringify({
          position: { x: 0, y: 0.01, z: 0 },
          rotation: { x: 0, y: 180, z: 0 }
        })
      };
      sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));
      console.log('[Sandbox] Teleport command sent to spawn');
      setIsAtPodium(false);
      setHasScreenSharePermission(false); // Reset screen share permission when leaving podium
    } catch (error) {
      console.error('[Sandbox] Error sending teleport command:', error);
    }
  };

  // Handle screen share state changes - auto-enable low-res mode when sharing from podium
  const handleScreenShareChange = (isSharing: boolean) => {
    console.log('[Sandbox] Screen share state changed:', isSharing);
    setIsLocalUserScreenSharing(isSharing); // Mute stage playback when presenting to avoid double audio
    if (isSharing && isAtPodium) {
      console.log('[Sandbox] Auto-enabling low-res mode for podium presenter');
      setIsLowResMode(true);
      // Set canvas resolution for resource savings
      if (canvasRef.current) {
        canvasRef.current.width = 640;
        canvasRef.current.height = 360;
      }
    }
  };

  // Handle toggling presentation mode (low-res mode for screen sharing)
  const handleTogglePresentationMode = () => {
    const newLowResMode = !isLowResMode;
    console.log('[Sandbox] Toggling presentation mode:', newLowResMode);
    setIsLowResMode(newLowResMode);
    
    // Actually change canvas resolution to save resources
    if (canvasRef.current) {
      if (newLowResMode) {
        canvasRef.current.width = 640;
        canvasRef.current.height = 360;
      } else {
        // Restore to container size
        canvasRef.current.width = canvasRef.current.clientWidth || window.innerWidth;
        canvasRef.current.height = canvasRef.current.clientHeight || window.innerHeight;
        
        // Scroll chat to bottom after exiting presentation mode (layout changes)
        setTimeout(() => {
          chatRef.current?.scrollToBottom();
        }, 100);
      }
    }
  };

  const avatarOptions = [
    { id: '54', characterName: 'IntersectCharacter1', image: avatarIcon54 },
    { id: '55', characterName: 'IntersectCharacter2', image: avatarIcon55 },
    { id: '56', characterName: 'IntersectCharacter3', image: avatarIcon56 },
    { id: '57', characterName: 'IntersectCharacter4', image: avatarIcon57 },
    { id: '58', characterName: 'IntersectCharacter5', image: avatarIcon58 },
    { id: '59', characterName: 'IntersectCharacter6', image: avatarIcon59 },
    { id: '60', characterName: 'IntersectCharacter7', image: avatarIcon60 },
    { id: '61', characterName: 'IntersectCharacter8', image: avatarIcon61 },
    { id: '62', characterName: 'IntersectCharacter9', image: avatarIcon62 },
    { id: '63', characterName: 'IntersectCharacter10', image: avatarIcon63 },
  ];

  // Make sendMessageToUnity available globally for widgets like SocialInteraction
  useEffect(() => {
    (window as any).sendMessageToUnity = sendMessageToUnity;
    console.log('[Sandbox] Global sendMessageToUnity stub registered');
    
    return () => {
      delete (window as any).sendMessageToUnity;
      console.log('[Sandbox] Global sendMessageToUnity stub removed');
    };
  }, []);

  // Listen for Ctrl+H to toggle UI visibility (hidden feature for all users)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && (event.key === 'h' || event.key === 'H')) {
        event.preventDefault(); // Prevent browser's default Ctrl+H behavior
        console.log('[Sandbox] Ctrl+H - toggling UI visibility');
        setIsUIVisible((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Listen for Terminal event (to test the full Unity→Browser flow)
  useEffect(() => {
    const handleTerminalEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const payload = customEvent.detail;
      
      console.log('[Sandbox Terminal] Event received:', payload, 'roomSelectorRef:', roomSelectorRef.current);
      
      try {
        const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
        const id = data?.id?.toLowerCase?.() || '';
        
        if (id === 'terminal') {
          console.log('[Sandbox Terminal] Opening Room Selector from terminal event');
          if (roomSelectorRef.current) {
            roomSelectorRef.current.open();
            console.log('[Sandbox Terminal] Room Selector open() called');
          } else {
            console.warn('[Sandbox Terminal] roomSelectorRef.current is null!');
          }
        } else {
          console.log('[Sandbox Terminal] Event id mismatch, got:', data?.id);
        }
      } catch (e) {
        // Maybe payload itself is just the id string
        if (payload === 'terminal' || payload?.toLowerCase?.() === 'terminal') {
          console.log('[Sandbox Terminal] Opening Room Selector from terminal (plain string)');
          if (roomSelectorRef.current) {
            roomSelectorRef.current.open();
          } else {
            console.warn('[Sandbox Terminal] roomSelectorRef.current is null!');
          }
        } else {
          console.error('[Sandbox Terminal] Error parsing event data:', e, 'payload:', payload);
        }
      }
    };

    window.addEventListener('Terminal', handleTerminalEvent);
    console.log('[Sandbox] Terminal event listener registered');
    
    return () => {
      window.removeEventListener('Terminal', handleTerminalEvent);
      console.log('[Sandbox] Terminal event listener removed');
    };
  }, []);

  const handleControlsClick = () => {
    setShowControlsPopup(true);
  };

  const handlePopupClose = () => {
    setShowControlsPopup(false);
  };

  const handleStageViewToggle = (enabled: boolean) => {
    setIsStageViewMode(enabled);
    console.log('[Stage View]', enabled ? 'Entering stage mode' : 'Exiting stage mode');
  };

  // Handle respawn confirmation
  const handleRespawn = () => {
    console.log('[Sandbox] Respawning player to spawn location');
    try {
      const teleportData = {
        eventType: 'Teleport',
        payload: JSON.stringify({
          position: { x: 0, y: 0.01, z: 0 },
          rotation: { x: 0, y: 180, z: 0 }
        })
      };
      sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));
      console.log('[Sandbox] Teleport command sent');
      
      // Close popup and banner, reset fall detection
      setShowRespawnPopup(false);
      setShowRespawnBanner(false);
      hasFallPopupShownRef.current = false;
      lastYPositionRef.current = null; // Reset to allow future fall detection
    } catch (error) {
      console.error('[Sandbox] Error sending teleport command:', error);
    }
  };

  const handleRespawnCancel = () => {
    setShowRespawnPopup(false);
    // Show persistent banner instead
    setShowRespawnBanner(true);
  };

  // Handle click on Unity canvas to re-enable capture
  const handleCanvasClick = () => {
    // Don't recapture if a UI overlay is active
    if (hasActiveOverlay) return;
    
    console.log('[Browser→Unity] Canvas clicked - re-enabling Unity capture');
    try {
      // Enable keyboard capture
      const captureKeyboardData = {
        eventType: 'CaptureKeyboard',
        payload: JSON.stringify({ capture: true })
      };
      sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
      console.log('[Browser→Unity] Enabled keyboard capture');

      // Enable cursor capture
      const captureCursorData = {
        eventType: 'CaptureCursor',
        payload: JSON.stringify({ capture: true })
      };
      sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
      console.log('[Browser→Unity] Enabled cursor capture');
    } catch (error) {
      console.error('[Browser→Unity] Error enabling Unity capture:', error);
    }
  };

  // Handle right-click on Unity canvas to re-enable capture
  const handleCanvasRightClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent context menu
    console.log('[Browser→Unity] Canvas right-clicked - re-enabling Unity capture');
    try {
      // Enable keyboard capture
      const captureKeyboardData = {
        eventType: 'CaptureKeyboard',
        payload: JSON.stringify({ capture: true })
      };
      sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
      console.log('[Browser→Unity] Enabled keyboard capture');

      // Enable cursor capture
      const captureCursorData = {
        eventType: 'CaptureCursor',
        payload: JSON.stringify({ capture: true })
      };
      sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
      console.log('[Browser→Unity] Enabled cursor capture');
    } catch (error) {
      console.error('[Browser→Unity] Error enabling Unity capture:', error);
    }
  };

  // Handle room change
  const handleRoomChange = (room: any, skipTeleport: boolean = false) => {
    console.log('[Sandbox] Switching to room:', room, skipTeleport ? '(no teleport)' : '(with teleport)');
    
    // Set transitioning state to show loading UI
    setIsRoomTransitioning(true);
    
    // Clear current state to force disconnect
    setChatRoomArn('');
    setIvsParticipantToken(null);
    setIvsStageArn(null);
    setIvsUserId(null);
    setIvsIsIntersectStaff(false);
    setStageArn('');
    setStagePlaybackUrl('');
    hasFetchedIVSToken.current = false;
    
    // Longer delay to ensure complete disconnect, then connect to new room
    setTimeout(() => {
      setCurrentRoomId(room.room_id);
      setCurrentRoomName(room.name);
      setChatRoomArn(room.chat_room_arn);
      setStageArn(room.stage_arn);
      setStagePlaybackUrl(room.stage_playback_url || '');
      // Note: isRoomTransitioning will be cleared after participant token is fetched
      
      // Teleport player to room's spawn location (unless skipTeleport is true for area triggers)
      if (!skipTeleport && room.spawn_position && room.spawn_rotation) {
        // Parse position string "(x, y, z)" into object { x, y, z }
        const posMatch = room.spawn_position.match(/\(([^,]+),\s*([^,]+),\s*([^)]+)\)/);
        const rotMatch = room.spawn_rotation.match(/\(([^,]+),\s*([^,]+),\s*([^)]+)\)/);
        
        if (posMatch && rotMatch) {
          const position = {
            x: parseFloat(posMatch[1]),
            y: parseFloat(posMatch[2]),
            z: parseFloat(posMatch[3])
          };
          const rotation = {
            x: parseFloat(rotMatch[1]),
            y: parseFloat(rotMatch[2]),
            z: parseFloat(rotMatch[3])
          };
          
          const teleportData = {
            eventType: 'Teleport',
            payload: JSON.stringify({
              position,
              rotation
            })
          };
          sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));
          console.log('[Sandbox] [Room Switch] Teleporting to:', position, rotation);
        } else {
          console.error('[Sandbox] [Room Switch] Failed to parse spawn position or rotation:', room.spawn_position, room.spawn_rotation);
        }
      } else if (skipTeleport) {
        console.log('[Sandbox] [Room Switch] Skipping teleport for area trigger room change');
      }
    }, 500);
  };

  // Fetch IVS participant token when room changes
  useEffect(() => {
    // Skip API calls entirely in mock mode
    if (MOCK_MODE) {
      console.log('[Sandbox] Mock mode active - skipping IVS token fetch');
      return;
    }
    
    if (!accessToken || propIvsStageArn || propIvsParticipantToken || hasFetchedIVSToken.current || ivsParticipantToken || !stageArn) {
      // If props are provided, no accessToken, already fetched, already have token, or no stage ARN, skip
      return;
    }

    let isMounted = true;

    const fetchIVSToken = async () => {
      hasFetchedIVSToken.current = true; // Mark as fetching to prevent duplicate calls
      
      if (isMounted) {
      setIsLoadingIVS(true);
      }

      try {
        const response = await API.getIVSParticipantTokenForRoom(accessToken, {
          eventId: eventId,
          roomId: currentRoomId
        });

        if (!isMounted) return; // Exit if component unmounted

        if (response.status !== 200) {
          console.error('Failed to fetch IVS participant token:', response.status);
          hasFetchedIVSToken.current = false; // Reset on error so we can retry
          if (isMounted) {
          setIsLoadingIVS(false);
          }
          return;
        }

        const data = await response.json();

        if (!isMounted) return; // Exit if component unmounted

        if (data.participantToken) {
          // Extract token, userId, intersectStaff, and stagePlaybackUrl from response
          const token = data.participantToken.token;
          const userId = data.participantToken.userId;
          const intersectStaff = data.participantToken.intersectStaff || false;
          const playbackUrl = data.participantToken.stagePlaybackUrl || '';
          
          if (isMounted) {
          setIvsParticipantToken(token);
          setIvsUserId(userId);
            setIvsIsIntersectStaff(intersectStaff);
            setIvsStageArn(stageArn); // Use the stageArn from current room
            setStagePlaybackUrl(playbackUrl);
            
            // Clear room transitioning state now that we have the new token
            setIsRoomTransitioning(false);
            console.log('[Sandbox] Room transition complete');
          }
        } else {
          // Clear transitioning state even if no token
          if (isMounted) {
            setIsRoomTransitioning(false);
          }
        }
      } catch (error) {
        console.error('Error fetching IVS participant token:', error);
        hasFetchedIVSToken.current = false; // Reset on error so we can retry
        // Clear transitioning state even on error
        if (isMounted) {
          setIsRoomTransitioning(false);
        }
      } finally {
        if (isMounted) {
        setIsLoadingIVS(false);
        }
      }
    };

    fetchIVSToken();

    return () => {
      isMounted = false;
    };
  }, [accessToken, propIvsStageArn, propIvsParticipantToken, ivsParticipantToken, stageArn, eventId]);

  // Auto-grant screenshare if user has active booking for current room
  // Also revoke if user leaves a room where they had auto-granted permission
  useEffect(() => {
    const checkBookingScreenshare = async () => {
      if (!currentRoomId || !ivsUserId || !accessToken) return;

      try {
        const response = await API.getBookings(accessToken, eventId);
        if (response.status !== 200) return;

        const data = await response.json();
        const bookings = data.bookings || [];
        const now = new Date();

        // Find active booking for current room owned by current user
        const activeBooking = bookings.find((booking: any) => {
          if (booking.room_id !== currentRoomId) return false;
          if (booking.booked_by_user_id !== ivsUserId) return false;
          if (booking.status === 'cancelled') return false;

          const startTime = new Date(booking.start_time);
          const endTime = new Date(booking.end_time);
          return now >= startTime && now <= endTime;
        });

        if (activeBooking) {
          // User has active booking for this room - grant permission
          console.log('[Sandbox] Auto-granting screenshare for active booking:', activeBooking.id, 'in room:', currentRoomId);
          screenShareAutoGrantedForRoom.current = currentRoomId;
          setHasScreenSharePermission(true);
        } else {
          // No active booking for this room
          // If permission was auto-granted for a different room, revoke it
          if (screenShareAutoGrantedForRoom.current && screenShareAutoGrantedForRoom.current !== currentRoomId) {
            console.log('[Sandbox] Revoking auto-granted screenshare - left booked room:', screenShareAutoGrantedForRoom.current, 'now in:', currentRoomId);
            screenShareAutoGrantedForRoom.current = null;
            setHasScreenSharePermission(false);
          }
        }
      } catch (error) {
        console.error('Error checking booking screenshare:', error);
      }
    };

    checkBookingScreenshare();
  }, [currentRoomId, ivsUserId, accessToken, eventId]);

  const handleFullscreenClick = async () => {
    if (!gameContainerRef.current) return;

    try {
      if (!isFullscreen) {
        // Enter fullscreen - use the game container so widgets are included
        if (gameContainerRef.current.requestFullscreen) {
          await gameContainerRef.current.requestFullscreen();
        } else if ((gameContainerRef.current as any).webkitRequestFullscreen) {
          await (gameContainerRef.current as any).webkitRequestFullscreen();
        } else if ((gameContainerRef.current as any).mozRequestFullScreen) {
          await (gameContainerRef.current as any).mozRequestFullScreen();
        } else if ((gameContainerRef.current as any).msRequestFullscreen) {
          await (gameContainerRef.current as any).msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Simulate loading progress
  useEffect(() => {
    if (!isLoading) return;

    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setIsLoading(false);
          return 100;
        }
        return prev + 2; // Increment by 2% every 100ms
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [isLoading]);

  // Image cycling effect for loading screen
  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      // Fade out
      setImageOpacity(0);
      
      // After fade out completes, change image and fade in
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => 
          (prevIndex + 1) % loadingImages.length
        );
        setImageOpacity(1);
      }, 500); // Half second for fade out
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [isLoading]);

  // Initialize WebGPU canvas
  useEffect(() => {
    if (canvasRef.current && !isLoading) {
      const canvas = canvasRef.current;
      
      const initWebGPU = async () => {
        try {
          // Check for WebGPU support
          if (!navigator.gpu) {
            // Fallback to WebGL or 2D canvas
            const ctx = canvas.getContext('2d');
            if (ctx) {
              canvas.width = canvas.offsetWidth;
              canvas.height = canvas.offsetHeight;
              const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
              gradient.addColorStop(0, '#1a1a2e');
              gradient.addColorStop(1, '#16213e');
              ctx.fillStyle = gradient;
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
              ctx.font = '24px Arial';
              ctx.textAlign = 'center';
              ctx.fillText('WebGPU not available - Using 2D fallback', canvas.width / 2, canvas.height / 2);
            }
            return;
          }

          // Request adapter and device
          const adapter = await navigator.gpu.requestAdapter();
          if (!adapter) {
            throw new Error('No WebGPU adapter found');
          }

          const device = await adapter.requestDevice();
          const context = canvas.getContext('webgpu');
          
          if (!context) {
            throw new Error('WebGPU context not available');
          }

          // Configure canvas format
          const format = navigator.gpu.getPreferredCanvasFormat();
          context.configure({
            device,
            format,
            alphaMode: 'opaque',
          });

          // Set canvas size
          const updateCanvasSize = () => {
            const displayWidth = canvas.clientWidth;
            const displayHeight = canvas.clientHeight;
            
            if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
              canvas.width = displayWidth;
              canvas.height = displayHeight;
            }
          };
          
          updateCanvasSize();
          
          // Handle resize
          const resizeObserver = new ResizeObserver(() => {
            updateCanvasSize();
          });
          resizeObserver.observe(canvas);

          // Render loop - simple colored background
          const render = () => {
            updateCanvasSize();
            const commandEncoder = device.createCommandEncoder();
            const textureView = context.getCurrentTexture().createView();
            
            const renderPassDescriptor: GPURenderPassDescriptor = {
              colorAttachments: [
                {
                  view: textureView,
                  clearValue: { r: 0.14, g: 0.12, b: 0.13, a: 1.0 }, // #231f20
                  loadOp: 'clear',
                  storeOp: 'store',
                },
              ],
            };

            const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
            passEncoder.end();

            device.queue.submit([commandEncoder.finish()]);
            requestAnimationFrame(render);
          };

          render();

          return () => {
            resizeObserver.disconnect();
          };
        } catch (error) {
          console.error('WebGPU initialization error:', error);
          // Fallback to 2D canvas
          const ctx = canvas.getContext('2d');
          if (ctx) {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
            const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            gradient.addColorStop(0, '#1a1a2e');
            gradient.addColorStop(1, '#16213e');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.font = '24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('WebGPU Error - Using 2D fallback', canvas.width / 2, canvas.height / 2);
          }
        }
      };

      initWebGPU();
    }
  }, [isLoading]);

  // Check intersect staff status from chat or use debug override
  useEffect(() => {
    if (debugMode) {
      // In debug mode, use the manual override
      setIsIntersectStaff(debugIntersectStaff);
    } else {
      // In normal mode, check from chat
      const checkIntersectStaff = () => {
        if (chatRef.current && chatRef.current.isIntersectStaff) {
          const staffStatus = chatRef.current.isIntersectStaff();
          setIsIntersectStaff(staffStatus);
        }
      };

      // Check initially after a short delay to let chat initialize
      const initialTimeout = setTimeout(checkIntersectStaff, 2000);

      // Check periodically every 5 seconds
      const interval = setInterval(checkIntersectStaff, 5000);

      return () => {
        clearTimeout(initialTimeout);
        clearInterval(interval);
      };
    }
  }, [debugMode, debugIntersectStaff]);

  // Show onboarding tour when avatar selection is complete (if not previously completed)
  useEffect(() => {
    if (isUnityWorldReady && !showAvatarSelection && !hasCompletedOnboarding()) {
      // Small delay to let the UI settle before showing tour
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isUnityWorldReady, showAvatarSelection]);

  // Handler to replay the onboarding tour
  const handleReplayTour = () => {
    resetOnboarding();
    setShowOnboarding(true);
  };

  // Handlers for onboarding tour completion
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
  };

  return (
    <div className="relative w-full h-full" onClick={handleCanvasClick}>
      <div
        id="unity-container"
        ref={containerRef}
        className="unity-desktop flex flex-col items-center justify-center relative h-[80%] w-full mx-auto"
      >
        <Helmet>
          <title>Unity Game Sandbox - Widget Test</title>
        </Helmet>
        
        <div 
          ref={gameContainerRef}
          className={`relative ${isLowResMode ? '' : 'w-full h-full'}`}
          style={{ 
            backgroundColor: '#231f20',
            // When in low-res mode, minimize this container since canvas is position:fixed
            ...(isLowResMode && { width: 0, height: 0, overflow: 'hidden' })
          }}
          onClick={handleCanvasClick}
          onContextMenu={handleCanvasRightClick}
        >
          {/* Unity Canvas - Always rendered */}
        <canvas 
          ref={canvasRef} 
          id="unity-canvas" 
            className="bg-[#231f20] cursor-pointer"
            style={isLowResMode ? {
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '240px',
              height: '135px',
              zIndex: 100,
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
            } : {
              width: '100%',
              height: '100%'
            }}
            onClick={handleCanvasClick}
            onContextMenu={handleCanvasRightClick}
        ></canvas>
        
          {/* Stage View Overlay - Covers canvas when active */}
          {isStageViewMode && (
            <div 
              className="absolute inset-0"
              style={{ zIndex: 5 }}
            >
              <StagePlayer
                playbackUrl={stagePlaybackUrl}
                isFullscreen={true}
                onClose={() => handleStageViewToggle(false)}
                isMuted={isStageMuted || isLocalUserScreenSharing}
                onToggleMute={() => setIsStageMuted(!isStageMuted)}
                className="w-full h-full"
              />
          </div>
        )}
        </div>
        
        {/* UI elements outside gameContainerRef so they're not affected by canvas position changes */}
        {!isFullscreen && isUIVisible && !isLowResMode && (
          <button
            id="unity-fullscreen-button"
            data-tour-id="tour-fullscreen-button"
            onClick={handleFullscreenClick}
            className="unity-custom-cursor p-2 bg-gray-800 bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-200"
            style={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 'var(--unity-fullscreen-button-bottom)',
              zIndex: 10000,
            }}
          >
            <FaExpand size={20} />
          </button>
        )}
        
        
        {/* Controls and Video Chat Widgets */}
        {!isLoading && isUIVisible && (
          <>
          <div
            className="unity-widgets-container unity-dock-tray unity-custom-cursor pointer-events-auto fixed z-10 transition-all duration-300 ease-in-out"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              visibility: isUnityWorldReady ? 'visible' : 'hidden',
              zIndex: 10000,
            }}
          >
            {isAtPodium && (
              <LeavePodium onLeavePodium={handleLeavePodium} />
            )}
            {!isPresenterMode && currentRoomId !== 'intersect-main' && (
              <BreakoutClapDock
                disabled={!accessToken || (!MOCK_MODE && !chatRoomArn)}
                onClap={handleSandboxBreakoutClap}
              />
            )}
            {!isPresenterMode && currentRoomId !== 'intersect-main' && (
              <SocialInteraction
                dataTourId="tour-highfive-button"
                disabled={!accessToken || (!MOCK_MODE && !chatRoomArn)}
                syncRaisedFromQueue={isLocalHandRaisedInQueue}
                onHandRaiseChange={async (raised) => {
                  if (MOCK_MODE) {
                    await new Promise((r) => setTimeout(r, 100));
                    return;
                  }
                  const chat = chatRef.current;
                  if (!chat?.sendHandRaiseState) throw new Error('Chat unavailable');
                  await chat.sendHandRaiseState(raised);
                }}
              />
            )}
            {/* Agenda with booking support */}
            <Agenda
              accessToken={accessToken}
              eventId={eventId}
              currentRoomId={currentRoomId}
              onRoomChange={handleRoomChange}
              cachedRooms={[
                { room_id: 'intersect-main', room_name: 'Intersect Main Room', event_id: eventId, chat_room_arn: chatRoomArn, stage_arn: stageArn },
                { room_id: 'intersect-room-01', room_name: 'Breakout Room 1', event_id: eventId, chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C', stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/PjvitesOthd1' },
                { room_id: 'intersect-room-02', room_name: 'Breakout Room 2', event_id: eventId, chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C', stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/PjvitesOthd1' },
                { room_id: 'intersect-room-03', room_name: 'Breakout Room 3', event_id: eventId, chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C', stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/PjvitesOthd1' },
                { room_id: 'intersect-room-04', room_name: 'Breakout Room 4', event_id: eventId, chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C', stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/PjvitesOthd1' },
              ]}
              isStaff={isIntersectStaff}
              currentUserId={ivsUserId || undefined}
            />
            {/* Hide room selector and controls in presenter mode */}
            {!isPresenterMode && (
              <div data-tour-id="tour-rooms-button">
                <RoomSelector
                  ref={roomSelectorRef}
                  dockMode
                  accessToken={accessToken}
                  eventId={eventId}
                  currentRoomId={currentRoomId}
                  onRoomChange={handleRoomChange}
                  cachedRooms={[
                    { room_id: 'intersect-main', room_name: 'Intersect Main Room', event_id: eventId, chat_room_arn: chatRoomArn, stage_arn: stageArn },
                    { room_id: 'intersect-room-01', room_name: 'Breakout Room 1', event_id: eventId, chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C', stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/PjvitesOthd1' },
                    { room_id: 'intersect-room-02', room_name: 'Breakout Room 2', event_id: eventId, chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C', stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/PjvitesOthd1' },
                    { room_id: 'intersect-room-03', room_name: 'Breakout Room 3', event_id: eventId, chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C', stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/PjvitesOthd1' },
                    { room_id: 'intersect-room-04', room_name: 'Breakout Room 4', event_id: eventId, chat_room_arn: 'arn:aws:ivschat:us-west-2:390409532441:room/Dnm0Rz9xUz4C', stage_arn: 'arn:aws:ivs:us-west-2:390409532441:stage/PjvitesOthd1' },
                  ]}
                  isLoadingRooms={false}
                  isStaff={isIntersectStaff}
                />
              </div>
            )}
            {/* ModTools - only show when room is fully loaded (keep visible for staff even in presenter mode) */}
            {ivsStageArn && ivsParticipantToken && isIntersectStaff && (
              <ModTools
                dockMode
                accessToken={accessToken}
                eventId={eventId}
                currentRoomId={currentRoomId}
                chatRef={chatRef}
                podiumPosition="(-0.524, 2.604, -41.375)"
                chatRoomAvailable={Boolean(chatRoomArn)}
                currentUserId={ivsUserId || undefined}
                onPodiumTeleport={() => setIsAtPodium(true)}
              />
            )}
            
            {/* Help widget - hide in presenter mode */}
            {!isPresenterMode && accessToken && eventId && (
              <RoomUsersWidget
                dockMode
                eventId={eventId}
                roomId={currentRoomId}
                accessToken={accessToken}
                currentUserId={ivsUserId || undefined}
                raisedHands={handRaiseNotificationItems}
                isIntersectStaff={isIntersectStaff}
                onModeratorUnraiseHand={
                  isIntersectStaff && accessToken && (MOCK_MODE || chatRoomArn)
                    ? handleModeratorUnraiseHand
                    : undefined
                }
              />
            )}
            {!isPresenterMode && (
              <HelpWidget
                dockMode
                dataTourId="tour-help-button"
                onReplayTour={handleReplayTour}
                onOpenKeyboardControls={handleControlsClick}
              />
            )}
            
            {(!isIVSPanelEnabled || isIVSPanelCollapsed || videoDockPopoverOpen) && (
              <div ref={videoDockTileRef}>
                <UnityGameDockApp
                  popoverOwnerId={videoDockPopoverOwnerId}
                  dataTourId="tour-video-dock"
                  ariaLabel={videoDockPopoverOpen ? 'Close video menu' : 'Video — preview and controls'}
                  label="Video"
                  icon={<FaVideo />}
                  active={videoDockPopoverOpen}
                  tileClassName="bg-gradient-to-br from-violet-600 to-fuchsia-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isIVSPanelEnabled) {
                      console.log('[Sandbox IVS Panel] Re-enabling panel and rejoining stream');
                      setIsIVSPanelEnabled(true);
                    }
                    setIsIVSPanelCollapsed(true);
                    setScreenShareDockPopoverOpen(false);
                    setVideoDockPopoverOpen((o) => {
                      if (o) {
                        localVideoControlApiRef.current?.closeCameraPreview?.();
                      }
                      return !o;
                    });
                  }}
                >
                  {videoDockPopoverOpen && (
                    <div
                      className="unity-dock-popover unity-dock-popover--enter unity-dock-popover--wide unity-dock-popover--video flex w-[min(96vw,28rem)] max-w-[28rem] flex-col gap-2 border-gray-700 bg-gray-900 p-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Video</div>
                      <div
                        ref={videoCameraPreviewHostRef}
                        className="w-full shrink-0 rounded-lg border border-gray-700/80 bg-black/40"
                      />
                      {!localVideoControlApi ? (
                        <p className="text-xs text-gray-500">Waiting for video session…</p>
                      ) : localVideoControlApi.isFetchingVideoCallToken ? (
                        <p className="flex items-center gap-2 text-xs text-gray-400">
                          <FaSpinner className="animate-spin" size={14} />
                          Checking room video…
                        </p>
                      ) : !localVideoControlApi.isVideoCallConnected ? (
                        <p className="text-xs text-gray-500">Video call not connected yet.</p>
                      ) : null}
                      {localVideoControlApi?.cameraPreviewError ? (
                        <p className="text-xs text-red-300">{localVideoControlApi.cameraPreviewError}</p>
                      ) : null}
                      {localVideoControlApi?.isAtVideoLimit && !localVideoControlApi.localVideoEnabled ? (
                        <p className="text-xs text-amber-400">Room video publisher limit reached.</p>
                      ) : null}
                      {localVideoControlApi?.isVideoCallConnected &&
                      localVideoControlApi.localVideoEnabled &&
                      !localVideoControlApi.isCameraPreviewOpen ? (
                        <>
                          <div className="relative aspect-video max-h-44 overflow-hidden rounded-lg border border-gray-600 bg-black">
                            <DockLocalVideoPreview
                              stream={localVideoControlApi.localStream}
                              enabled={localVideoControlApi.localVideoEnabled}
                            />
                            {localVideoControlApi.localVideoLoading ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                                <FaSpinner className="animate-spin text-white" size={28} />
                              </div>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => void localVideoControlApi.toggleLocalVideo()}
                            className="flex w-full items-center justify-center gap-2 rounded-md border border-blue-500 bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-500"
                          >
                            <FaVideo />
                            Turn off camera
                          </button>
                        </>
                      ) : null}
                      {localVideoControlApi &&
                      !localVideoControlApi.localVideoEnabled &&
                      !localVideoControlApi.isCameraPreviewOpen &&
                      !localVideoControlApi.isFetchingVideoCallToken &&
                      !(localVideoControlApi.isVideoCallAvailable && localVideoControlApi.isAtVideoLimit) ? (
                        <button
                          type="button"
                          onClick={() => localVideoControlApi.openCameraPreview()}
                          className="w-full rounded-md border border-gray-600 bg-[#2a2a2a] px-3 py-2 text-sm font-medium text-white hover:bg-[#333]"
                        >
                          Camera preview…
                        </button>
                      ) : null}
                    </div>
                  )}
                </UnityGameDockApp>
              </div>
            )}

            {!isPresenterMode && (hasScreenSharePermission || Boolean(screenShareControlApi?.screenShareEnabled)) && (
              <div ref={screenShareDockTileRef}>
                <UnityGameDockApp
                  popoverOwnerId={screenShareDockPopoverOwnerId}
                  dataTourId="tour-screenshare-dock"
                  ariaLabel={screenShareDockPopoverOpen ? 'Close screen share menu' : 'Screen share'}
                  label="Share"
                  icon={<FaDesktop />}
                  active={screenShareDockPopoverOpen}
                  tileClassName="bg-gradient-to-br from-emerald-700 to-teal-900"
                  onClick={(e) => {
                    e.stopPropagation();
                    setVideoDockPopoverOpen(false);
                    if (!isIVSPanelEnabled) {
                      setIsIVSPanelEnabled(true);
                    }
                    setIsIVSPanelCollapsed(true);
                    setScreenShareDockPopoverOpen((o) => !o);
                  }}
                >
                  {screenShareDockPopoverOpen && (
                    <div
                      className="unity-dock-popover unity-dock-popover--enter unity-dock-popover--wide flex w-[min(92vw,20rem)] max-w-[20rem] flex-col gap-3 border-gray-700 bg-gray-900 p-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Screen sharing</div>
                      {!screenShareControlApi ? (
                        <p className="text-sm text-gray-500">Waiting for video session…</p>
                      ) : (
                        <>
                          {!screenShareControlApi.screenShareEnabled && (
                            <div className="flex gap-2" data-tour-id="tour-screenshare-quality">
                              <select
                                value={screenShareControlApi.screenShareQuality}
                                onChange={(e) =>
                                  screenShareControlApi.setScreenShareQuality(e.target.value as ScreenShareQuality)
                                }
                                className="flex-1 cursor-pointer rounded-md border border-[#444] bg-[#2a2a2a] p-2.5 text-sm text-white focus:border-blue-500 focus:outline-none"
                                disabled={
                                  !screenShareControlApi.isScreenShareStageConnected ||
                                  screenShareControlApi.screenShareLoading
                                }
                              >
                                {(Object.keys(SCREEN_SHARE_QUALITY_PRESETS) as ScreenShareQuality[]).map((key) => (
                                  <option key={key} value={key}>
                                    {SCREEN_SHARE_QUALITY_PRESETS[key].label} (
                                    {SCREEN_SHARE_QUALITY_PRESETS[key].description})
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          <button
                            type="button"
                            className={`flex w-full items-center justify-center gap-2 rounded-md p-2.5 text-base text-white transition-all duration-200 ${
                              screenShareControlApi.screenShareEnabled
                                ? 'border border-green-500 bg-green-500 hover:bg-green-600'
                                : 'border border-[#444] bg-[#2a2a2a] hover:border-[#555] hover:bg-[#3a3a3a]'
                            } ${
                              !screenShareControlApi.isScreenShareStageConnected ||
                              screenShareControlApi.screenShareLoading
                                ? 'cursor-not-allowed opacity-50'
                                : ''
                            }`}
                            onClick={() => void screenShareControlApi.toggleScreenShare()}
                            disabled={
                              !screenShareControlApi.isScreenShareStageConnected ||
                              screenShareControlApi.screenShareLoading
                            }
                            title={
                              !screenShareControlApi.isScreenShareStageConnected
                                ? 'Screen share stage not connected'
                                : screenShareControlApi.screenShareEnabled
                                  ? 'Stop sharing screen'
                                  : `Share your screen (${SCREEN_SHARE_QUALITY_PRESETS[screenShareControlApi.screenShareQuality].description})`
                            }
                            data-tour-id="tour-screenshare-button"
                          >
                            {screenShareControlApi.screenShareLoading ? (
                              <FaSpinner className="animate-spin" />
                            ) : (
                              <FaDesktop />
                            )}
                            <span className="text-sm font-medium">
                              {screenShareControlApi.screenShareLoading
                                ? 'Starting…'
                                : screenShareControlApi.screenShareEnabled
                                  ? 'Stop sharing'
                                  : 'Share screen'}
                            </span>
                          </button>
                          {screenShareControlApi.hasScreenSharePermission ? (
                            <button
                              type="button"
                              className={`flex w-full items-center justify-center gap-2 rounded-md p-2.5 text-base text-white transition-all duration-200 ${
                                screenShareControlApi.isPresentationMode
                                  ? 'border border-purple-500 bg-purple-500 hover:bg-purple-600'
                                  : 'border border-[#444] bg-[#2a2a2a] hover:border-[#555] hover:bg-[#3a3a3a]'
                              }`}
                              onClick={() => screenShareControlApi.togglePresentationMode()}
                              title={
                                screenShareControlApi.isPresentationMode
                                  ? 'Exit presentation mode'
                                  : 'Presentation mode (reduces graphics for smoother screen share)'
                              }
                              data-tour-id="tour-presentation-mode"
                            >
                              {screenShareControlApi.isPresentationMode ? <FaExpand /> : <FaCompress />}
                              <span className="text-sm font-medium">
                                {screenShareControlApi.isPresentationMode
                                  ? 'Exit presentation mode'
                                  : 'Presentation mode'}
                              </span>
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  )}
                </UnityGameDockApp>
              </div>
            )}
          </div>
          {!isPresenterMode && currentRoomId !== 'intersect-main' && (
            <BreakoutClapOverlay bursts={clapBursts} />
          )}
          </>
        )}

          {/* IVS Realtime Panel - Overlay on top of Unity game/stage */}
          {/* Only render when enabled - unmounting completely disconnects and cleans up streams */}
          {isUIVisible && isIVSPanelEnabled && ((ivsStageArn && ivsParticipantToken) || isRoomTransitioning) ? (
            <div 
              style={{ 
              visibility: isUnityWorldReady ? 'visible' : 'hidden',
              // Always use fixed positioning for consistent layout regardless of presentation mode
              position: 'fixed' as const,
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              pointerEvents: 'none',
              zIndex: 1000
            }}>
              <IVSRealtimePanel
                key={`panel-${currentRoomId}`}
                stageArn={ivsStageArn || ''}
                participantToken={ivsParticipantToken || ''}
                accessToken={accessToken}
                userId={ivsUserId || undefined}
                isIntersectStaff={ivsIsIntersectStaff}
                externalCollapseControl={true}
                isCollapsedExternal={isIVSPanelCollapsed}
                onCollapseChange={setIsIVSPanelCollapsed}
                onDisablePanel={() => {
                  console.log('[Sandbox IVS Panel] Disabling panel - component will unmount');
                  setIsIVSPanelEnabled(false);
                }}
                onScreenShareChange={handleScreenShareChange}
                hasScreenSharePermission={hasScreenSharePermission}
                isPresentationMode={isLowResMode}
                onTogglePresentationMode={handleTogglePresentationMode}
                roomName={currentRoomName}
                debugMode={false}
                isLoading={isRoomTransitioning}
                stagePlaybackUrl={stagePlaybackUrl}
                isStageViewActive={isStageViewMode}
                isStageMuted={isStageMuted}
                onToggleStageMute={() => setIsStageMuted(!isStageMuted)}
                eventId={eventId}
                roomId={currentRoomId}
                dataTourId="tour-video-panel"
                onExposeLocalVideoControls={setLocalVideoControlApi}
                onExposeScreenShareControls={setScreenShareControlApi}
                onScreenShareTutorialRequest={handleScreenShareTutorialRequest}
                cameraPreviewHostRef={videoCameraPreviewHostRef}
                cameraPreviewHostReady={videoDockPopoverOpen}
                dockCameraUx
                onCameraPreviewDismissInDock={() => setVideoDockPopoverOpen(false)}
                onRequestVideoDock={openVideoDockFromFacePin}
              />
            </div>
          ) : null}

          {/* IVS Realtime Chat - Overlay on bottom left */}
          {isUIVisible && chatRoomArn && (
            <div 
              style={{ 
              visibility: isUnityWorldReady ? 'visible' : 'hidden',
              // Always use fixed positioning for consistent layout regardless of presentation mode
              position: 'fixed' as const,
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              pointerEvents: 'none',
              zIndex: 1000
            }}>
              <IVSRealtimeChat
                key={`chat-${currentRoomId}`}
                ref={chatRef}
                roomArn={chatRoomArn}
                messagingEndpoint="wss://edge.ivschat.us-west-2.amazonaws.com"
                accessToken={accessToken}
                userId={ivsUserId || undefined}
                roomName={currentRoomName}
                roomId={currentRoomId}
                eventId={eventId}
                podiumPosition="(-0.524, 2.604, -41.375)"
                debugIsIntersectStaff={debugIntersectStaff}
                onPodiumTeleport={() => setIsAtPodium(true)}
                onScreenSharePermissionGranted={() => {
                  console.log('[Sandbox] Screen share permission granted!');
                  setHasScreenSharePermission(true);
                }}
                onScreenSharePermissionRevoked={() => {
                  console.log('[Sandbox] Screen share permission revoked!');
                  screenShareAutoGrantedForRoom.current = null; // Clear auto-grant tracking
                  setHasScreenSharePermission(false);
                }}
                onHandRaiseSignal={handleHandRaiseSignal}
                onClapSignal={handleClapSignal}
                handRaiseDisplayName={handRaiseDisplayName}
                dataTourId="tour-chat-panel"
              />
            </div>
          )}

        {/* NPC Dialog */}
        {showNPCDialog && !isLoading && (
          <NPCDialog
            npcId={currentNPCId}
            currentMessage={(npcDialogues[currentNPCId] || [])[currentNPCMessageIndex] || ''}
            messageIndex={currentNPCMessageIndex}
            totalMessages={(npcDialogues[currentNPCId] || []).length}
            onNext={handleNPCNext}
            onClose={handleNPCClose}
          />
        )}

        {/* Avatar Selection Modal - Highest Priority (after world ready, same as dock UI) */}
        {showAvatarSelection && !isLoading && isUnityWorldReady && (
          <div 
            className="absolute inset-0 bg-black bg-opacity-90 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 10002 }}
          >
            <div 
              className="relative bg-gray-900 border-2 border-blue-500 rounded-lg p-8 max-w-2xl mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-3xl font-bold text-white mb-4 text-center">
                Select Your Avatar
              </h2>
              <p className="text-gray-300 mb-8 text-center">
                {isCustomizingAvatar ? 'Customizing your avatar...' : 'Choose an avatar to get started'}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                {avatarOptions.map((avatar) => (
              <button
                    key={avatar.id}
                    onClick={() => customizeActiveAvatar(avatar.id, avatar.characterName)}
                    disabled={isCustomizingAvatar}
                    className={`group relative flex flex-col items-center p-4 bg-gray-800 border-2 border-gray-700 rounded-lg transition-all duration-200 ${
                      isCustomizingAvatar 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:border-blue-500 hover:bg-gray-700 transform hover:scale-105 cursor-pointer'
                    }`}
                  >
                    <img
                      src={avatar.image}
                      alt={`Avatar ${avatar.id}`}
                      className="w-24 h-24 object-contain mb-3"
                    />
                    {isCustomizingAvatar && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 rounded-lg">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Controls Popup Modal */}
        {showControlsPopup && !isLoading && !showAvatarSelection && (
          <div 
            className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={handlePopupClose}
            style={{ zIndex: 10001 }}
          >
            <div className="relative max-w-4xl max-h-[90%] p-4 flex items-center justify-center">
              <img
                src={tutorialPanel}
                alt="Controls"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on image
              />
              
              <button
                onClick={handlePopupClose}
                className="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors flex items-center justify-center text-lg font-bold z-50"
              >
                ×
              </button>
            </div>
          </div>
        )}
            
        {/* Respawn Confirmation Popup */}
        {showRespawnPopup && !isLoading && (
            <div 
            className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
              onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 10001 }}
          >
            <div 
              className="relative bg-gray-900 border-2 border-orange-500 rounded-lg p-8 max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-white mb-4 text-center">
                Respawn to Spawn Point?
              </h2>
              <p className="text-gray-300 mb-6 text-center">
                Would you like to teleport back to the spawn location?
              </p>
              <div className="flex gap-4">
                <button
                  onClick={handleRespawn}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-semibold"
                >
                  Yes, Respawn
                </button>
                <button
                  onClick={handleRespawnCancel}
                  className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 font-semibold"
                >
                  No, Thanks
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Respawn Banner - Persistent reminder */}
        {showRespawnBanner && !isLoading && !showRespawnPopup && (
          <div 
            className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-orange-600 bg-opacity-90 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-3"
            style={{ zIndex: 10001 }}
          >
            <span className="text-sm font-semibold">
              Press "R" to teleport back to spawn
            </span>
            <button
              onClick={() => setShowRespawnBanner(false)}
              className="ml-2 text-white hover:text-gray-200 transition-colors"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Onboarding Tour */}
        {showOnboarding && !isLoading && !showAvatarSelection && (
          <OnboardingTour
            isVisible={showOnboarding}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
          />
        )}

        {/* Sandbox Debug Button - Simulate Fall */}
        {/* {!isLoading && isUIVisible && (
          <div 
            className="fixed bottom-20 left-4 flex flex-col gap-2 z-50"
            style={{ zIndex: 10001 }}
          >
            <button
              onClick={() => {
                console.log('[Sandbox] Simulating fall detection');
                setShowRespawnPopup(true);
                hasFallPopupShownRef.current = true;
              }}
              className="px-4 py-2 bg-red-600 bg-opacity-80 text-white rounded-lg hover:bg-opacity-100 transition-all duration-200 font-semibold text-sm"
              title="Test fall detection"
            >
              🪂 Test Fall
            </button>
          </div>
        )} */}
        {isLoading && (
          <div className="absolute inset-0 bg-black z-20" style={{ zIndex: 10002 }}>
            <img
              src={loadingImages[currentImageIndex]}
              alt="Loading"
              className="w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
              style={{ opacity: imageOpacity }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            </div>
          </div>
        )}
        {isLoading && (
          <div
            id="unity-loading-bar"
            className="absolute bottom-16 left-4 right-4 h-2 bg-gray-200 rounded-full overflow-hidden z-20"
            style={{ zIndex: 10003 }}
          >
            <div
              id="unity-progress-bar-full"
              className="h-full bg-blue-500 transition-all duration-300 ease-out"
              style={{ width: `${loadingProgress}%` }}
            ></div>
          </div>
        )}
        
        {/* Debug Controls - Bottom Center */}
        {!isLoading && isUIVisible && debugMode && (
          <div 
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10 bg-yellow-400 bg-opacity-90 px-4 py-2 rounded-lg border-2 border-yellow-600"
            style={{ zIndex: 10000 }}
          >
            <span className="text-black font-semibold">DEBUG:</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDebugIntersectStaff(!debugIntersectStaff);
              }}
              className={`px-3 py-1 rounded font-semibold transition-all duration-200 ${
                debugIntersectStaff
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
              title="Toggle Intersect Staff Status (Debug)"
            >
              {debugIntersectStaff ? '✓ Intersect Staff' : '✗ Regular User'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                testNPCDialog();
              }}
              className="px-3 py-1 rounded font-semibold transition-all duration-200 bg-cyan-600 text-white hover:bg-cyan-700"
              title="Test NPC Dialog"
            >
              💬 Test NPC Dialog
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('[Sandbox Debug] Testing Room Selector open(), ref:', roomSelectorRef.current);
                if (roomSelectorRef.current) {
                  roomSelectorRef.current.open();
                  console.log('[Sandbox Debug] Room Selector open() called');
                } else {
                  console.warn('[Sandbox Debug] roomSelectorRef.current is null!');
                }
              }}
              className="px-3 py-1 rounded font-semibold transition-all duration-200 bg-green-600 text-white hover:bg-green-700"
              title="Test Room Selector (Direct)"
            >
              🚪 Open Room Selector
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('[Sandbox Debug] Dispatching Terminal event');
                const terminalEvent = new CustomEvent('Terminal', { 
                  detail: JSON.stringify({ id: 'terminal' }) 
                });
                window.dispatchEvent(terminalEvent);
                console.log('[Sandbox Debug] Terminal event dispatched');
              }}
              className="px-3 py-1 rounded font-semibold transition-all duration-200 bg-orange-600 text-white hover:bg-orange-700"
              title="Test Terminal Event (Full Flow)"
            >
              📡 Dispatch Terminal
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('[Sandbox Debug] Starting onboarding tour');
                resetOnboarding();
                setShowOnboarding(true);
              }}
              className="px-3 py-1 rounded font-semibold transition-all duration-200 bg-indigo-600 text-white hover:bg-indigo-700"
              title="Test Onboarding Tour"
            >
              🎓 Test Tour
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const names = ['Alex Chen', 'Jordan Smith', 'Sam Rivera'];
                const displayName = names[Math.floor(Math.random() * names.length)];
                handleHandRaiseSignal({
                  userId: `sandbox-demo-${Date.now()}`,
                  displayName,
                  raised: true,
                  at: Date.now(),
                });
              }}
              className="px-3 py-1 rounded font-semibold transition-all duration-200 bg-amber-600 text-white hover:bg-amber-700"
              title="Add a sample entry to the People → Raised hands queue"
            >
              Sample raised hand
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setHandRaiseByUserId({});
              }}
              className="px-3 py-1 rounded font-semibold transition-all duration-200 bg-stone-600 text-white hover:bg-stone-700"
              title="Clear all raised-hand queue entries (local)"
            >
              Clear raised hands
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const names = ['Alex Chen', 'Jordan Smith', 'Sam Rivera'];
                const displayName = names[Math.floor(Math.random() * names.length)];
                handleClapSignal({
                  userId: `sandbox-clap-${Date.now()}`,
                  displayName,
                  at: Date.now(),
                });
              }}
              className="px-3 py-1 rounded font-semibold transition-all duration-200 bg-indigo-500 text-white hover:bg-indigo-600"
              title="Simulate another participant clapping (overlay + Who clapped list)"
            >
              Sample clap
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('[Sandbox Debug] Toggling screen share permission');
                setHasScreenSharePermission(!hasScreenSharePermission);
              }}
              className={`px-3 py-1 rounded font-semibold transition-all duration-200 ${
                hasScreenSharePermission
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
              title="Toggle Screen Share Permission"
            >
              {hasScreenSharePermission ? '✓ Screen Share ON' : '✗ Screen Share OFF'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('[Sandbox Debug] Resetting screen share tutorial');
                resetScreenShareTutorial();
                // Also turn on permission to trigger the tutorial
                setHasScreenSharePermission(true);
              }}
              className="px-3 py-1 rounded font-semibold transition-all duration-200 bg-pink-600 text-white hover:bg-pink-700"
              title="Reset & Test Screen Share Tutorial"
            >
              🖥️ Test Screen Share Tour
            </button>
          </div>
        )}
      </div>
        {!isFullscreen && (
          <div className="mt-4 space-y-2">
            <div className="p-4 bg-yellow-100 border border-yellow-400 rounded-lg text-yellow-800">
              <strong>Sandbox Mode:</strong> This is a test environment for widgets and controls. WebGPU canvas with fullscreen testing.
              {isFullscreen && <div className="mt-2 text-green-600">✓ Currently in fullscreen mode</div>}
            </div>
            {MOCK_MODE && (
              <div className="p-4 bg-orange-100 border border-orange-400 rounded-lg text-orange-800">
                <strong>⚠️ Mock Mode Active:</strong> Using mock IVS data - API calls are bypassed. 
                Video/chat connections won't work, but you can test UI features like the <strong>Screen Share Tutorial</strong>.
                <div className="mt-2 text-sm">
                  • IVS Panel will render but won't connect to real streams<br/>
                  • Chat panel will render but messages won't send<br/>
                  • <strong>Raise hand</strong> toggles in the top bar still work (simulated); use DEBUG <strong>Sample raised hand</strong> / <strong>Sample clap</strong> to preview peer notifications<br/>
                  • Screen share tutorial and UI controls are fully testable
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

// Wrapper component that provides FocusProvider
const UnityGameSandbox: React.FC<UnityGameSandboxProps> = (props) => {
  return (
    <FocusProvider>
      <UnityGameSandboxInner {...props} />
    </FocusProvider>
  );
};

export default UnityGameSandbox;

